import { forwardRef, ForwardedRef, useEffect, useState } from 'react';
import { ButtonLeft } from '@zoopla-eng/zds-components/button';
import { Badge } from '@zoopla-eng/zds-components/badges';
import { SavedMedium, SaveMedium } from '@zoopla-eng/zds-components/icons';
import {
    AuthModal,
    AuthStepper,
    useAuthStepper
} from '@zoopla/authentication/modal';
import { TrackedProperty } from '../../graphql.generated';

import { SpecifyRelationshipModal } from '../SpecifyRelationshipModal/specify-relationship-modal';
import {
    SpecifyRelationshipForm,
    SpecifyRelationshipModalProps
} from '../SpecifyRelationshipModal/specify-relationship-form';
import type { PropertyDataForAnalytics } from '@zoopla/analytics/property-data';
import { mapRelationship } from '@zoopla/map-relationship';
import { getUPRNfromQuery } from '@zoopla/property-from-query';

import { InfoPanel } from '@zoopla-eng/zds-components/info-panel';
import { useTrackProperty } from '../hooks/useTrackProperty';
import { useSaveRelationship } from '../hooks/useSaveRelationship';
import { useRouter } from 'next/router';
import {
    buttonAndRelationshipWrapperVariantStyle,
    buttonWrapperStyle,
    styledConsentStyle
} from './Button.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { trackRelationshipFormShown } from '../tracking';

type ButtonProps = {
    analyticsData: PropertyDataForAnalytics;
    trackedProperty: TrackedProperty;
    hideLabel?: boolean;
    setIsAuthModalShowing: (state: boolean) => void;
};

export const consentText = `You'll get a monthly valuation email, plus updates about this property and the local area.`;
const consentId = '6';

const stepOrder = ['auth', 'onboarding', 'relationship-modal'];

const SpecifyRelationshipFormStep = (props: SpecifyRelationshipModalProps) => {
    const { moveToNextStep } = useAuthStepper();

    useEffect(() => {
        trackRelationshipFormShown(props.action);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <SpecifyRelationshipForm {...props} onSaved={moveToNextStep} />;
};

const Button = forwardRef(
    (
        {
            analyticsData,
            trackedProperty,
            hideLabel = false,
            setIsAuthModalShowing
        }: ButtonProps,
        ref: ForwardedRef<HTMLButtonElement | null>
    ) => {
        const [isRelationshipModalShowing, updateIsRelationshipModalShowing] =
            useState(false);

        const consentData = {
            consentId,
            consentText
        };

        const {
            isTracked,
            handleTrackButtonClick,
            trackOrUntrackProperty,
            isAuthModalShowing,
            updateIsAuthModalShowing,
            loading,
            error
        } = useTrackProperty(
            analyticsData,
            trackedProperty,
            isRelationshipModalShowing,
            updateIsRelationshipModalShowing,
            consentData
        );

        const {
            dismissRelationshipModal,
            saveAndCloseRelationshipModal,
            relationship,
            saveRelationshipLoading,
            saveRelationshipError
        } = useSaveRelationship(
            analyticsData,
            updateIsRelationshipModalShowing
        );

        const Icon = isTracked ? SavedMedium : SaveMedium;

        const visibleLabel = isTracked ? 'Tracking' : 'Track';

        const ariaLabel = isTracked
            ? 'Remove from tracked properties'
            : 'Track this property';

        const router = useRouter();
        const uprn = getUPRNfromQuery(router.query);

        useEffect(() => {
            if (isAuthModalShowing) {
                setIsAuthModalShowing(true);
            }
        }, [isAuthModalShowing, setIsAuthModalShowing]);

        return (
            <>
                <AuthModal<'relationship-modal'>
                    isShowing={isAuthModalShowing}
                    modalAuthHeading="Track properties"
                    close={() => updateIsAuthModalShowing(false)}
                    initialMode="register"
                    additionalStepsTitlesMap={{
                        'relationship-modal': {
                            title: 'Set your relationship to this property',
                            trackingCategory: 'track property relationship'
                        }
                    }}
                    additionalSteps={
                        <AuthStepper.Step id="relationship-modal">
                            <SpecifyRelationshipFormStep
                                action={{
                                    saveAndCloseRelationshipModal,
                                    formShownEventLabel: 'save property',
                                    formShownEventValue: uprn
                                        ? parseInt(uprn, 10)
                                        : 0
                                }}
                                isSubmissionLoading={saveRelationshipLoading}
                                submissionError={saveRelationshipError}
                            />
                        </AuthStepper.Step>
                    }
                    actionAfterAuth={trackOrUntrackProperty}
                    initialStepsOrder={stepOrder}
                    trackingData={{
                        label: 'save_property',
                        segmentData: {
                            page: '/property/details/',
                            section: 'home-values',
                            activity: 'property_details'
                        }
                    }}
                />
                <SpecifyRelationshipModal
                    action={{
                        dismissRelationshipModal,
                        saveAndCloseRelationshipModal,
                        formShownEventLabel: 'edit relationship',
                        formShownEventCategory: 'tracked property details',
                        formShownEventValue: uprn ? parseInt(uprn, 10) : 0
                    }}
                    isShowing={isRelationshipModalShowing}
                    isSubmissionLoading={saveRelationshipLoading}
                    submissionError={saveRelationshipError}
                    existingRelationship={relationship}
                />
                <div className={buttonAndRelationshipWrapperVariantStyle}>
                    {!isTracked && (
                        <p
                            id="track-property-consent"
                            className={`${styledConsentStyle} ${fontGroups.ladle}`}
                        >
                            {consentText}
                        </p>
                    )}
                    <div className={buttonWrapperStyle}>
                        <ButtonLeft
                            data-testid="track-this-property-button"
                            category="primary"
                            fw
                            onClick={handleTrackButtonClick}
                            aria-label={ariaLabel}
                            loading={loading}
                            loadingText={visibleLabel}
                            aria-describedby="track-property-consent"
                            ref={ref}
                            Icon={Icon}
                        >
                            {visibleLabel}
                        </ButtonLeft>
                    </div>

                    {!hideLabel && relationship && (
                        <Badge level={2} text={mapRelationship(relationship)} />
                    )}
                </div>
                {error && (
                    <InfoPanel type="failure" strapline="Error">
                        Something went wrong when tracking this property. Please
                        try again.
                    </InfoPanel>
                )}
            </>
        );
    }
);

export default Button;
